import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  DefaultButton,
  PrimaryButton,
  IconButton,
  TextField,
  Icon,
} from '@fluentui/react'
import { apiService } from '../services/apiService'
import { useApi } from '../hooks/useApi'
import { useDashboardTokens, type DashboardTokens } from '../theme/dashboardTokens'
import { isWeekend, shiftDate, todayIso } from '../utils/date'
import type { ChangeAcknowledgment, ChangeDetail, ModifiedApp, ChangedApp } from '../types'

/**
 * Routed day-detail view (v2, Lane B / V10).
 *
 * Replaces v1's `DayDetailPanel` side-panel with the locked v2 pattern: route
 * *into* the day. A sticky breadcrumb `<nav>` (Dashboard › <date>) carries the
 * context + a Back button + the day's mini-tally; the body reuses
 * `getChangeDetail` to list Added / Changed (field-level) / Credential /
 * Removed. App names link onward to the existing Applications list filtered to
 * that app (`/applications?search=…`) — true per-app *detail* + credential
 * deep-links are deferred until the change payload carries the internal id
 * (folded into the data-model expansion; see TASKS).
 *
 * Reached from the chart's `onDayClick` and the recent-changes feed rows, both
 * routing to `/changes/:date`.
 *
 * Absorbs the day-detail work Alex built on the v1 panel (PR #20): the
 * review/acknowledge workflow, per-app owner context, per-field risk dots,
 * friendly field labels, the weekend warning, and prev/next day nav. Those
 * arrive here re-themed onto the v2 tokens so they follow light/dark; the panel
 * they came from is deleted.
 */

// API field names (snake_case) → human-readable labels.
const FIELD_LABELS: Record<string, string> = {
  display_name: 'Display name',
  redirect_uris: 'Redirect URIs',
  preferred_sso_mode: 'SSO mode',
  app_roles: 'App roles',
  key_credentials_count: 'Certificates',
  password_credentials_count: 'Client secrets',
  identifier_uris: 'Identifier URIs',
  logout_url: 'Logout URL',
  homepage: 'Homepage URL',
  reply_urls: 'Reply URLs',
  tags: 'Tags',
  sign_in_audience: 'Sign-in audience',
  account_enabled: 'Account enabled',
  publisher_domain: 'Publisher domain',
  verified_publisher: 'Verified publisher',
}

/**
 * Risk weighting per field. Anything that changes *who can redirect to the app*
 * or *what can authenticate as it* is high — those are the fields an attacker
 * would touch. Cosmetic metadata is low.
 */
type RiskLevel = 'high' | 'medium' | 'low'
const FIELD_RISK: Record<string, RiskLevel> = {
  redirect_uris: 'high',
  reply_urls: 'high',
  identifier_uris: 'high',
  key_credentials_count: 'high',
  password_credentials_count: 'high',
  preferred_sso_mode: 'high',
  sign_in_audience: 'high',
  app_roles: 'medium',
  logout_url: 'medium',
  homepage: 'medium',
  account_enabled: 'medium',
  display_name: 'low',
  tags: 'low',
  publisher_domain: 'low',
  verified_publisher: 'low',
}

function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function riskColor(level: RiskLevel, tokens: DashboardTokens): string {
  if (level === 'high') return tokens.removed
  if (level === 'medium') return tokens.amber
  return tokens.added
}

function formatDay(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

function stringify(value: unknown): string {
  if (value == null) return '∅'
  if (Array.isArray(value)) return value.length ? value.join(', ') : '∅'
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  return String(value)
}

export function ChangeDayView() {
  const { date = '' } = useParams<{ date: string }>()
  const navigate = useNavigate()
  const tokens = useDashboardTokens()

  const fetchDetail = useCallback(() => apiService.getChangeDetail(date), [date])
  const { data, loading, error } = useApi<ChangeDetail>(fetchDetail, [date])

  // The diff pipeline can't produce a day that hasn't happened, so don't let the
  // user page into one.
  const atLatestDay = !date || date >= todayIso()
  const goToDay = useCallback(
    (offset: number) => {
      if (!date) return
      if (offset > 0 && atLatestDay) return
      // `replace` so paging through days doesn't stack history entries — Back
      // should return to wherever you entered from (dashboard / feed), not walk
      // back through every day you stepped past.
      navigate(`/changes/${shiftDate(date, offset)}`, { replace: true })
    },
    [date, atLatestDay, navigate],
  )

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      // Don't hijack arrows while the user is typing a review note.
      const el = e.target as HTMLElement | null
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        goToDay(-1)
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        goToDay(+1)
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [goToDay])

  const openApp = (app: { applicationId?: number; display_name?: string }) => {
    if (app.applicationId) {
      navigate(`/applications/${app.applicationId}`)
    } else if (app.display_name) {
      navigate(`/applications?search=${encodeURIComponent(app.display_name)}`)
    }
  }

  const cred = data?.credentialChanges
  const credTotal = cred?.total ?? 0
  const tally = data
    ? [
        { label: 'Added', value: data.added.length, color: tokens.added },
        { label: 'Changed', value: data.modified.length, color: tokens.changed },
        { label: 'Credential', value: credTotal, color: tokens.cred },
        { label: 'Removed', value: data.removed.length, color: tokens.removed },
      ]
    : []
  const nothing =
    !!data && data.added.length === 0 && data.removed.length === 0 && data.modified.length === 0 && credTotal === 0

  return (
    <div>
      {/* Sticky breadcrumb / context strip */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          background: tokens.bg,
          borderBottom: `1px solid ${tokens.line}`,
          padding: '12px 4px',
          marginBottom: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <DefaultButton
            iconProps={{ iconName: 'Back' }}
            text="Back"
            onClick={() => navigate(-1)}
            styles={{ root: { height: 30 } }}
          />
          <nav aria-label="Breadcrumb">
            <ol style={{ display: 'flex', alignItems: 'center', gap: 8, listStyle: 'none', margin: 0, padding: 0 }}>
              <li>
                <Link to="/" style={{ color: tokens.changed, textDecoration: 'none', fontSize: 14 }}>
                  Dashboard
                </Link>
              </li>
              <li aria-hidden style={{ color: tokens.muted }}>
                /
              </li>
              <li aria-current="page" style={{ color: tokens.text, fontSize: 14, fontWeight: 600 }}>
                {date ? formatDay(date) : 'Changes'}
              </li>
            </ol>
          </nav>

          {/* Prev / next day */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
            <IconButton
              iconProps={{ iconName: 'ChevronLeft' }}
              title="Previous day (←)"
              ariaLabel="Previous day"
              onClick={() => goToDay(-1)}
              styles={{ root: { height: 30 } }}
            />
            <IconButton
              iconProps={{ iconName: 'ChevronRight' }}
              title={atLatestDay ? 'No later day yet' : 'Next day (→)'}
              ariaLabel="Next day"
              onClick={() => goToDay(+1)}
              disabled={atLatestDay}
              styles={{ root: { height: 30 } }}
            />
          </div>
        </div>

        {/* Mini-tally */}
        {data && !nothing ? (
          <div style={{ display: 'flex', gap: 16, marginTop: 10, flexWrap: 'wrap' }}>
            {tally.map((t) => (
              <span key={t.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <span style={{ width: 9, height: 9, borderRadius: 2, background: t.color }} />
                <span style={{ fontWeight: 700, color: tokens.text }}>{t.value}</span>
                <span style={{ color: tokens.slate }}>{t.label}</span>
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 900 }}>
        {date && isWeekend(date) ? (
          <MessageBar messageBarType={MessageBarType.warning}>
            Weekend change — activity outside normal business days is worth a closer look.
          </MessageBar>
        ) : null}

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <Spinner size={SpinnerSize.large} label="Loading changes…" />
          </div>
        ) : error ? (
          <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
        ) : data ? (
          <>
            {nothing ? (
              <div style={{ color: tokens.muted, fontSize: 14 }}>
                {data.note || 'No changes recorded for this day.'}
              </div>
            ) : null}

            {data.added.length > 0 ? (
              <Section title="Added" count={data.added.length} color={tokens.added} tokens={tokens}>
                {data.added.map((app) => (
                  <AppRow key={app.object_id} app={app} tokens={tokens} onOpen={() => openApp(app)} />
                ))}
              </Section>
            ) : null}

            {data.modified.length > 0 ? (
              <Section title="Changed" count={data.modified.length} color={tokens.changed} tokens={tokens}>
                {data.modified.map((app) => (
                  <ModifiedRow key={app.object_id} app={app} tokens={tokens} onOpen={() => openApp(app)} />
                ))}
              </Section>
            ) : null}

            {credTotal > 0 && cred ? (
              <Section title="Credential changes" count={credTotal} color={tokens.cred} tokens={tokens}>
                <CredLine label="Certificates added" value={cred.certAdded} tokens={tokens} />
                <CredLine label="Certificates removed" value={cred.certRemoved} tokens={tokens} />
                <CredLine label="Secrets added" value={cred.secretAdded} tokens={tokens} />
                <CredLine label="Secrets removed" value={cred.secretRemoved} tokens={tokens} />
              </Section>
            ) : null}

            {data.removed.length > 0 ? (
              <Section title="Removed" count={data.removed.length} color={tokens.removed} tokens={tokens}>
                {data.removed.map((app) => (
                  <AppRow key={app.object_id} app={app} tokens={tokens} onOpen={() => openApp(app)} />
                ))}
              </Section>
            ) : null}
          </>
        ) : null}

        {date && !loading ? <ReviewLog date={date} tokens={tokens} /> : null}
      </div>
    </div>
  )
}

function Section({
  title,
  count,
  color,
  tokens,
  children,
}: {
  title: string
  count: number
  color: string
  tokens: DashboardTokens
  children: ReactNode
}) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 11, height: 11, borderRadius: 2, background: color }} />
        <h2 style={{ fontSize: 15, fontWeight: 600, color: tokens.text, margin: 0 }}>
          {title} <span style={{ color: tokens.muted, fontWeight: 400 }}>({count})</span>
        </h2>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingLeft: 19 }}>{children}</div>
    </section>
  )
}

/** Owner context per app — an unowned app in a change feed is itself a finding. */
function OwnerTag({ name, email, tokens }: { name?: string; email?: string; tokens: DashboardTokens }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
      <span style={{ color: tokens.muted }}>Owner:</span>
      {name ? (
        <span title={email ?? undefined} style={{ color: tokens.slate }}>
          {name}
        </span>
      ) : (
        <span style={{ color: tokens.removed, fontWeight: 600 }}>Unowned</span>
      )}
    </span>
  )
}

function RiskDot({ field, tokens }: { field: string; tokens: DashboardTokens }) {
  const level = FIELD_RISK[field]
  if (!level) return null
  const label = `${level.charAt(0).toUpperCase()}${level.slice(1)} risk`
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      style={{
        display: 'inline-block',
        flexShrink: 0,
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: riskColor(level, tokens),
      }}
    />
  )
}

function AppRow({ app, tokens, onOpen }: { app: ChangedApp; tokens: DashboardTokens; onOpen: () => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <button
        type="button"
        onClick={onOpen}
        aria-label={`${app.display_name || 'unnamed app'}. View in applications.`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          alignSelf: 'flex-start',
          background: 'transparent',
          border: 'none',
          borderRadius: 6,
          padding: '2px 4px',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 14, color: tokens.changed, fontWeight: 500 }}>
          {app.display_name || '(unnamed app)'}
        </span>
        {app.preferred_sso_mode ? (
          <span style={{ fontSize: 11, color: tokens.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {app.preferred_sso_mode}
          </span>
        ) : null}
        <Icon iconName="OpenInNewWindow" style={{ fontSize: 11, color: tokens.muted }} aria-hidden />
      </button>
      <span style={{ paddingLeft: 6 }}>
        <OwnerTag name={app.primaryOwnerName} email={app.primaryOwnerEmail} tokens={tokens} />
      </span>
    </div>
  )
}

function ModifiedRow({ app, tokens, onOpen }: { app: ModifiedApp; tokens: DashboardTokens; onOpen: () => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={onOpen}
          aria-label={`${app.display_name || 'unnamed app'}. View in applications.`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: 'transparent',
            border: 'none',
            padding: '2px 4px',
            cursor: 'pointer',
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 600, color: tokens.changed }}>
            {app.display_name || '(unnamed app)'}
          </span>
          <Icon iconName="OpenInNewWindow" style={{ fontSize: 11, color: tokens.muted }} aria-hidden />
        </button>
        <OwnerTag name={app.primaryOwnerName} email={app.primaryOwnerEmail} tokens={tokens} />
      </div>
      {app.changes.map((c, i) => (
        <span
          key={`${c.field}-${i}`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: tokens.slate, paddingLeft: 10 }}
        >
          <RiskDot field={c.field} tokens={tokens} />
          <span>
            <strong style={{ color: tokens.text, fontWeight: 600 }}>{fieldLabel(c.field)}</strong>:{' '}
            {stringify(c.old_value)} → {stringify(c.new_value)}
          </span>
        </span>
      ))}
    </div>
  )
}

function CredLine({ label, value, tokens }: { label: string; value: number; tokens: DashboardTokens }) {
  if (!value) return null
  return (
    <span style={{ fontSize: 13, color: tokens.slate }}>
      {label}: <strong style={{ color: tokens.text }}>{value}</strong>
    </span>
  )
}

/**
 * Review / acknowledge workflow (Alex, PR #20 — backed by
 * GET/POST /api/reports/acknowledge + migration 011).
 *
 * Lets the IAM team mark a day's changes as reviewed with an optional note and
 * ticket number, and shows who has reviewed it already. This is what turns the
 * change log from a report into a governance record.
 */
function ReviewLog({ date, tokens }: { date: string; tokens: DashboardTokens }) {
  const [acks, setAcks] = useState<ChangeAcknowledgment[]>([])
  const [loadingAcks, setLoadingAcks] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [note, setNote] = useState('')
  const [ticket, setTicket] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoadingAcks(true)
    apiService
      .getAcknowledgments(date)
      .then((res) => {
        if (!cancelled) setAcks(res.acknowledgments)
      })
      .catch(() => {
        /* non-fatal — the section just stays empty */
      })
      .finally(() => {
        if (!cancelled) setLoadingAcks(false)
      })
    return () => {
      cancelled = true
    }
  }, [date])

  const handleSubmit = async () => {
    setSubmitting(true)
    setSubmitError(null)
    try {
      const created = await apiService.createAcknowledgment(date, note || undefined, ticket || undefined)
      setAcks((prev) => [created, ...prev])
      setNote('')
      setTicket('')
      setShowForm(false)
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Failed to save review')
    } finally {
      setSubmitting(false)
    }
  }

  const formatAckDate = (iso: string) =>
    new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10, borderTop: `1px solid ${tokens.line}`, paddingTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, color: tokens.text, margin: 0 }}>Review log</h2>
        {!showForm ? (
          <DefaultButton
            text="+ Add review"
            onClick={() => setShowForm(true)}
            styles={{ root: { minWidth: 'auto', height: 28, padding: '0 10px', fontSize: 12 } }}
          />
        ) : null}
      </div>

      {loadingAcks ? <span style={{ fontSize: 13, color: tokens.muted }}>Loading…</span> : null}

      {!loadingAcks && acks.length === 0 && !showForm ? (
        <span style={{ fontSize: 13, color: tokens.muted }}>No reviews yet.</span>
      ) : null}

      {acks.map((ack) => (
        <div
          key={ack.acknowledgmentId}
          style={{ display: 'flex', flexDirection: 'column', gap: 2, borderLeft: `3px solid ${tokens.added}`, paddingLeft: 8 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: tokens.text }}>{ack.acknowledgedBy}</span>
            <span style={{ fontSize: 12, color: tokens.muted }}>{formatAckDate(ack.acknowledgedAt)}</span>
            {ack.ticketNumber ? (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: tokens.changedOn,
                  background: tokens.changedSoft,
                  padding: '1px 6px',
                  borderRadius: 3,
                }}
              >
                {ack.ticketNumber}
              </span>
            ) : null}
          </div>
          {ack.note ? <span style={{ fontSize: 13, color: tokens.slate }}>{ack.note}</span> : null}
        </div>
      ))}

      {showForm ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            background: tokens.card2,
            padding: 12,
            borderRadius: 4,
          }}
        >
          <TextField
            label="Ticket # (optional)"
            placeholder="e.g. CHG-4421"
            value={ticket}
            onChange={(_, v) => setTicket(v ?? '')}
            maxLength={100}
            styles={{ root: { maxWidth: 200 } }}
          />
          <TextField
            label="Note (optional)"
            placeholder="e.g. Planned cert rotation — approved by security team"
            multiline
            rows={2}
            value={note}
            onChange={(_, v) => setNote(v ?? '')}
            maxLength={1000}
          />
          {submitError ? <MessageBar messageBarType={MessageBarType.error}>{submitError}</MessageBar> : null}
          <div style={{ display: 'flex', gap: 8 }}>
            <PrimaryButton text="Save review" onClick={handleSubmit} disabled={submitting} />
            <DefaultButton
              text="Cancel"
              onClick={() => {
                setShowForm(false)
                setSubmitError(null)
              }}
              disabled={submitting}
            />
          </div>
        </div>
      ) : null}
    </section>
  )
}

export default ChangeDayView
